import { mergeAbortSignals } from "@captions-web/utils/abort-signals";
import { QueryFunction, QueryKey } from "@tanstack/react-query";
import { z } from "zod";

import {
  BaseRequestCompletionContext,
  CallerCombinedVariables,
  CallerCombinedVariablesForPagination,
  QueryPaginationParams,
} from "./definitions/types";
import { queryRequester, QueryRequesterParams, ExtractParamsFromPath } from "./requester";

type ParamsExtractorFn<TQueryKey extends QueryKey> = (
  queryKey: TQueryKey
) => Record<string, unknown>;

export interface DefaultQueryFetcherOptions<
  TQueryKey extends QueryKey,
  Path extends string,
  ResponseSchema extends z.ZodTypeAny,
  ParamsSchema extends z.ZodTypeAny,
  Result,
> extends Omit<
    QueryRequesterParams<Path, ParamsSchema, ResponseSchema, Result>,
    "params" | "pathParams"
  > {
  extractParams?: ParamsExtractorFn<TQueryKey>;
  extractPathParams?: () => Record<string, string>;

  onSuccess?: (result: Result, context: BaseRequestCompletionContext) => void;
  onError?: (error: Error, context: BaseRequestCompletionContext) => void;
}

export function makeDefaultQueryFetcher<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
>({
  extractParams,
  extractPathParams,
  onSuccess,
  onError,
  ...requestOptions
}: DefaultQueryFetcherOptions<
  TQueryKey,
  Path,
  ParamsSchema,
  ResponseSchema,
  Result
>): QueryFunction<Result, TQueryKey> {
  return async ({ signal, queryKey }) => {
    const start = performance.now();
    try {
      const result = await queryRequester({
        ...requestOptions,
        signal: mergeAbortSignals(signal, requestOptions.signal),
        params: extractParams?.(queryKey),
        pathParams: extractPathParams?.() as ExtractParamsFromPath<Path>,
      });

      await onSuccess?.(result, { networkDurationMs: Math.ceil(performance.now() - start) });

      return result;
    } catch (error) {
      onError?.(error as Error, { networkDurationMs: Math.ceil(performance.now() - start) });
      throw error;
    }
  };
}

export interface PaginatedQueryFetcherOptions<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
  ParamsForPagination extends Partial<z.infer<ParamsSchema>>,
  PageParam,
> extends Omit<
    DefaultQueryFetcherOptions<TQueryKey, Path, ParamsSchema, ResponseSchema, Result>,
    "extractParams" | "extractPathParams"
  > {
  mapPaginationParamsToQueryParams: (
    paginationParams: QueryPaginationParams<PageParam>
  ) => ParamsForPagination;

  variables:
    | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
    | Record<string, never>;
}

export function makePaginatedQueryFetcher<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
  ParamsForPagination extends Partial<z.infer<ParamsSchema>>,
  PageParam,
>({
  variables,
  onSuccess,
  onError,
  mapPaginationParamsToQueryParams,
  ...requestOptions
}: PaginatedQueryFetcherOptions<
  TQueryKey,
  Path,
  ParamsSchema,
  ResponseSchema,
  Result,
  ParamsForPagination,
  PageParam
>): QueryFunction<Result, TQueryKey, PageParam> {
  const { params, pathParams } = extractVariables(variables);

  function mergeParams(
    params: z.infer<ParamsSchema> | undefined,
    paginationParams: QueryPaginationParams<PageParam>
  ) {
    return {
      ...params,
      ...mapPaginationParamsToQueryParams(paginationParams),
    };
  }

  return async ({ signal, pageParam }) => {
    const start = performance.now();
    try {
      const result = await queryRequester({
        ...requestOptions,
        signal: mergeAbortSignals(signal, requestOptions.signal),
        params: mergeParams(params, { pageParam: pageParam as PageParam }),
        pathParams,
      });

      await onSuccess?.(result, { networkDurationMs: Math.ceil(performance.now() - start) });

      return result;
    } catch (error) {
      onError?.(error as Error, { networkDurationMs: Math.ceil(performance.now() - start) });
      throw error;
    }
  };
}

export interface DefaultMutationFetcherParams<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
> extends Omit<
    QueryRequesterParams<Path, ParamsSchema, ResponseSchema, Result>,
    "params" | "signal" | "pathParams"
  > {
  paramsSchema?: ParamsSchema;

  getSignal?: () => AbortSignal | undefined;

  onSuccess?: (
    result: Result,
    variables: z.infer<ParamsSchema>,
    context: BaseRequestCompletionContext
  ) => Promise<void> | void;
  onError?: (
    error: Error,
    variables: z.infer<ParamsSchema>,
    context: BaseRequestCompletionContext
  ) => void;
}

export function makeDefaultMutationFetcher<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
>({
  paramsSchema,
  getSignal,
  onSuccess,
  onError,
  ...requestOptions
}: DefaultMutationFetcherParams<Path, ParamsSchema, ResponseSchema, Result>) {
  return async (variables: CallerCombinedVariables<Path, ParamsSchema>) => {
    const { params, pathParams } = extractVariables(variables);
    const start = performance.now();

    try {
      const result = await queryRequester({
        params: paramsSchema?.parse(params),
        pathParams,
        signal: getSignal?.(),
        ...requestOptions,
      });

      await onSuccess?.(result, variables, {
        networkDurationMs: Math.ceil(performance.now() - start),
      });

      return result;
    } catch (error) {
      onError?.(error as Error, variables, {
        networkDurationMs: Math.ceil(performance.now() - start),
      });
      throw error;
    }
  };
}

export interface PollingFetcherParamsWithVariables<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
> {
  signal: AbortSignal;
  variables: CallerCombinedVariables<Path, ParamsSchema>;
}

export function makeDefaultPollingFetcher<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result,
>({
  paramsSchema,
  getSignal,
  onSuccess,
  onError,
  ...requestOptions
}: DefaultMutationFetcherParams<Path, ParamsSchema, ResponseSchema, Result>) {
  return async ({ variables, signal }: PollingFetcherParamsWithVariables<Path, ParamsSchema>) => {
    const { params, pathParams } = extractVariables(variables);

    const combinedSignal = mergeAbortSignals(signal, getSignal?.());

    const start = performance.now();

    try {
      const result = await queryRequester({
        params: paramsSchema?.parse(params),
        pathParams,
        signal: combinedSignal,
        ...requestOptions,
      });

      await onSuccess?.(result, variables, {
        networkDurationMs: Math.ceil(performance.now() - start),
      });

      return result;
    } catch (error) {
      onError?.(error as Error, variables, {
        networkDurationMs: Math.ceil(performance.now() - start),
      });
      throw error;
    }
  };
}

export function extractVariables<Path extends string, ParamsSchema extends z.ZodTypeAny>(
  // eslint-disable-next-line @typescript-eslint/ban-types
  variables: CallerCombinedVariables<Path, ParamsSchema> | {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { params: any; pathParams: any } {
  const params = "params" in variables ? variables.params : {};
  const pathParams = "pathParams" in variables ? variables.pathParams : {};
  return { params, pathParams };
}
